import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { RxAttachmentsApiService } from '@shared/rx-attachments/rx-attachments-api.service';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { RxBannerComponent } from '@shared/components/rx-banner/rx-banner.component';
import { RxIntuitiveMissingAttachmentsBannerComponent } from '@modules/rx-intuitive-art/components/rx-intuitive-missing-attachments-banner/rx-intuitive-missing-attachments-banner.component';
import { RxIntuitivePatientNotLinkedBannerComponent } from '@modules/rx-intuitive-art/components/rx-intuitive-patient-not-linked-banner/rx-intuitive-patient-not-linked-banner.component';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, from, map, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, shareReplay, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { PatientFacade } from '@modules/patient/patient.facade';

@Component({
	selector: 'rx-intuitive-art-banners-orchestrator',
	templateUrl: './rx-intuitive-art-banners-orchestrator.component.html',
	styleUrls: ['./rx-intuitive-art-banners-orchestrator.component.scss'],
	standalone: true,
	providers: [PatientFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AsyncPipe,
		NgIf,
		TranslateModule,
		RxBannerComponent,
		RxIntuitivePatientNotLinkedBannerComponent,
		RxIntuitiveMissingAttachmentsBannerComponent
	]
})
export class RxIntuitiveArtBannersOrchestratorComponent extends BaseDestroyableComponent implements OnInit {
	@Input() attachmentsListUpdated$!: Observable<void>;

	showOfflineBanner$!: Observable<boolean>;
	showPatientNotLinkedBanner$!: Observable<boolean>;
	showOrderMissingAttachmentsBanner$!: Observable<boolean>;
	isPatientNotLinkedBannerDismissed = false;
	isOrderMissingAttachmentsBannerDismissed = false;

	ngOnInit(): void {
		this.showOfflineBanner$ = this.hostPlatformService.isOfflineModeOnScanner$ ?? of(false);

		// Currently it is not possible to reliably check if the patient is linked
		// Banner is disabled until linked patient api is improved
		this.showPatientNotLinkedBanner$ = of(false);

		this.patientFacade.patient$
			.pipe(
				map(patient => patient?.UID ?? null),
				distinctUntilChanged(),
				takeUntil(this.componentAlive$)
			)
			.subscribe(() => {
				this.isPatientNotLinkedBannerDismissed = false;
			});

		this.shellQuery.attachmentsRxId$
			.pipe(
				distinctUntilChanged(),
				takeUntil(this.componentAlive$)
			)
			.subscribe(() => {
				this.isOrderMissingAttachmentsBannerDismissed = false;
			});

		this.showOrderMissingAttachmentsBanner$ = combineLatest([
			this.showOfflineBanner$,
			this.showPatientNotLinkedBanner$,
			this.shellQuery.attachmentsRxId$,
			this.attachmentsListUpdated$.pipe(startWith(null))
		]).pipe(
			switchMap(([isOffline, isNotLinked, attachmentsRxId]) => {
				if (isOffline || isNotLinked || !attachmentsRxId) {
					return of(false);
				}
				return from(this.rxAttachmentsApiService.getAttachmentsCount([attachmentsRxId])).pipe(
					map(counts => counts.some(item => item.rxId === attachmentsRxId && item.attachmentsCount === 0)),
					catchError(() => of(false))
				);
			}),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	constructor(
		private readonly patientFacade: PatientFacade,
		private readonly hostPlatformService: HostPlatformService,
		private readonly shellQuery: ShellQuery,
		private readonly rxAttachmentsApiService: RxAttachmentsApiService
	) {
		super();
	}

	dismissPatientNotLinkedBanner(): void {
		this.isPatientNotLinkedBannerDismissed = true;
	}

	dismissOrderMissingAttachmentsBanner(): void {
		this.isOrderMissingAttachmentsBannerDismissed = true;
	}
}
